import { Component, OnInit, OnDestroy } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { FeatureService } from "../services/features.service";
import { ScreenSizeService } from "../services/screen-size.service";
import { CacheService } from "../services/cache.service";
import { lastValueFrom, map, Subscription } from "rxjs";
import { NetworkService } from "../services/network.service";

@Component({
  selector: "app-react-native-bundle",
  templateUrl: "./react-native-bundle.component.html",
  styleUrls: ["./react-native-bundle.component.scss"],
})
export class ReactNativeBundleComponent implements OnInit, OnDestroy {
  isMobile: boolean;
  isDesktop: boolean;
  featureId: string;
  baseUrl: string;
  homeName: string;

  networkSubscription: Subscription | null = null;
  online = true;

  bundleLoaded = false;

  constructor(
    private route: ActivatedRoute,
    private featureService: FeatureService,
    private screenSizeService: ScreenSizeService,
    private cacheService: CacheService,
    private networkService: NetworkService
  ) {
    console.log("Online status: " + this.networkService.isOnline());
    this.online = this.networkService.isOnline();
    this.networkSubscription = this.networkService
      .getOnlineStatus()
      .subscribe((isOnline) => {
        console.log("Network status changed:", isOnline);
        this.online = isOnline;
      });

    this.screenSizeService.isMobile$.subscribe((isMobile) => {
      this.isMobile = isMobile;
    });

    this.screenSizeService.isDesktop$.subscribe((isDesktop) => {
      this.isDesktop = isDesktop;
    });
  }

  ngOnInit(): void {
    this.resetSafeAreaInsets();

    if (!this.isDesktop) {
      document.body.classList.add("react-native-header-background");
      document.body.classList.add("react-native-footer-background");
    }

    console.log("React Native: Loading screen");
    this.route.paramMap.subscribe((params) => {
      this.featureId = params.get("featureId");
      console.log(`React Native getting feature id: ${this.featureId}`);
      this.featureService
        .getFeatureDescriptionByBasePath(this.featureId)
        .then((rslt) => {
          console.log("React Native got feature descriptions");
          sessionStorage.setItem("feature-base-path", rslt.baseUrl);
          this.baseUrl = rslt.baseUrl;
          this.homeName = rslt.homeName;

          const navigationParam =
            this.route.snapshot.queryParamMap.get("navigationParam");
          const uuid = this.route.snapshot.queryParamMap.get("uuid");

          (window as any).initialNavigation = navigationParam;
          (window as any).featureId = uuid;

          console.log("React Native loading react native bundle");
          this.loadReactNativeBundle(
            this.featureId,
            navigationParam,
            rslt.offline === "true"
          );
        });
    });
  }

  ngOnDestroy(): void {
    document.body.classList.remove("react-native-header-background");
    document.body.classList.remove("react-native-footer-background");
    if (this.networkSubscription) {
      this.networkSubscription.unsubscribe();
    }
  }

  resetSafeAreaInsets() {
    const htmlElement = document.documentElement;
    const innerHeight = window.innerHeight;
    htmlElement.style.setProperty("--client-height", `${innerHeight}px`);
  }

  async loadReactNativeBundle(
    featureId: string,
    navigationParam: string,
    isOffline: boolean
  ): Promise<void> {
    try {
      console.log("React Native feature id: " + featureId);

      // 1. Fetch the HTML
      const indexUrl = `${this.baseUrl}index.html`;
      const indexResponse = await lastValueFrom(
        this.cacheService
          .getResourceAndOnlyCacheIfNative(indexUrl)
          .pipe(map((response) => response?.data))
      );

      console.log("React Native HTML content:", indexResponse);

      if (!indexResponse) {
        throw new Error("Failed to load index.html");
      }

      document.getElementById("react-native-body").innerHTML = indexResponse;

      // 2. Fetch the JS bundle as text (or arrayBuffer, below)
      const bundleUrl = `${this.baseUrl}bundle.js`;
      let jsContent: string;

      console.log("Getting React Native bundle URL: " + bundleUrl);
      if (isOffline) {
        jsContent = await lastValueFrom(
          this.cacheService
            .getResource(bundleUrl, true)
            .pipe(map((response) => response?.data))
        );
      } else {
        jsContent = await lastValueFrom(
          this.cacheService
            .getResourceAndOnlyCacheIfNative(bundleUrl)
            .pipe(map((response) => response?.data))
        );
      }

      console.log(`React Native JS content: ${JSON.stringify(jsContent)}`);

      // 3. Create a Blob of type text/javascript
      const blob = new Blob([jsContent], { type: "text/javascript" });

      // 4. Create a temporary URL for that blob
      const scriptUrl = URL.createObjectURL(blob);

      console.log(`React Native script URL: ${JSON.stringify(scriptUrl)}`);

      // 5. Append <script> tag
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.src = scriptUrl;
      script.onload = () => {
        // Do your post-load logic
        this.adjustDocumentHeight();
        this.bundleLoaded = true;
        console.log("React Native bundle loaded successfully");
      };
      script.onerror = () => {
        console.error("Failed to load the script:", script.src);
        this.networkService.checkInternetConnection();
      };

      document.body.appendChild(script);
    } catch (error) {
      console.error("Error loading React app:", error);
      this.networkService.checkInternetConnection();
    }
  }

  private adjustDocumentHeight() {
    const computedStyle = getComputedStyle(document.documentElement);
    const htmlElement = document.documentElement;
    const clientHeight = computedStyle.getPropertyValue("--client-height");
    htmlElement.style.height = clientHeight.trim();
    console.log("React app loaded successfully, setting bundleLoaded to true");
  }
}
